import type { DefaultTimeFormatEnum, Team } from 'api-client';
import { OrgsApi } from 'api-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Key } from 'react-aria-components';
import { toast } from 'sonner';

import { getApiConfiguration } from '@/api/utils';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { Select } from '@/lib/base/forms/select/select';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import { logger } from '@/lib/logger';
import {
  DEFAULT_TIME_FORMAT,
  TIME_FORMAT_OPTIONS,
} from '@/lib/time/timeFormat';
import { useViewHealthMarker } from '@/lib/view-health';

import { useOrg } from '../../../../contexts/OrgContext';
import { TEAMS_ADMIN_PERMISSION } from '../settings-permissions';

const MAX_ORG_NAME_LENGTH = 100;

const TIME_FORMAT_SELECT_ITEMS = TIME_FORMAT_OPTIONS.map(option => ({
  id: option.value,
  label: option.label,
}));

function OrgNameForm({ canEdit }: { canEdit: boolean }) {
  const { currentOrg, updateOrgFields } = useOrg();

  const savedName = currentOrg?.name ?? '';
  const [name, setName] = useState(savedName);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const currentOrgIdRef = useRef(currentOrg?.id);

  const trimmed = name.trim();
  const isInvalid =
    trimmed.length === 0 || trimmed.length > MAX_ORG_NAME_LENGTH;
  const hasChanges = trimmed !== savedName;

  useEffect(() => {
    setName(savedName);
  }, [currentOrg?.id, savedName]);

  useEffect(() => {
    currentOrgIdRef.current = currentOrg?.id;
  }, [currentOrg?.id]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleNameChange = useCallback((value: string) => {
    setName(value);
    setError(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!currentOrg?.id) {
      setError('Organization not found');
      return;
    }

    if (isInvalid) {
      setError('Workspace name must be between 1 and 100 characters.');
      return;
    }

    const requestOrgId = currentOrg.id;
    const patch: Partial<Team> = { name: trimmed };
    setIsSaving(true);
    setError(null);

    try {
      const api = new OrgsApi(getApiConfiguration());
      await api.teamsPartialUpdate(
        { id: requestOrgId, patchedTeam: patch },
        { signal: AbortSignal.timeout(30000) }
      );

      updateOrgFields(requestOrgId, patch);

      if (isMountedRef.current && currentOrgIdRef.current === requestOrgId) {
        toast.success('Workspace name saved');
      }
    } catch (err: unknown) {
      logger.error(
        'Failed to update org name',
        err instanceof Error ? err : new Error(String(err)),
        { org_id: requestOrgId }
      );
      if (isMountedRef.current && currentOrgIdRef.current === requestOrgId) {
        setError("We couldn't save the workspace name. Try again.");
        toast.error("We couldn't update the workspace name. Try again.");
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [currentOrg, isInvalid, trimmed, updateOrgFields]);

  return (
    <div>
      <div className='space-y-4'>
        <Input
          label='Workspace name'
          value={name}
          onChange={handleNameChange}
          isDisabled={isSaving || !canEdit}
          isInvalid={!!error}
          maxLength={MAX_ORG_NAME_LENGTH}
        />

        {!canEdit && (
          <p className='text-sm text-fg-tertiary'>
            Only workspace admins can rename the workspace.
          </p>
        )}

        {error && <p className='text-sm text-error-primary'>{error}</p>}
      </div>

      {canEdit && (
        <div className='mt-4 flex justify-end'>
          <Button
            color='primary'
            size='md'
            onClick={handleSave}
            isDisabled={!hasChanges || isSaving || isInvalid}
            isLoading={isSaving}
          >
            Save changes
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Workspace-default time format (12h/24h). Members without a personal
 * preference (Personal Info → Time format → "Workspace default") inherit
 * this value. Mirrors DefaultCurrencyForm's save flow; the edit gate matches
 * the server-side admin guard (TeamSerializer.validate_default_time_format).
 */
function DefaultTimeFormatForm({ canEdit }: { canEdit: boolean }) {
  const { currentOrg, updateOrgFields } = useOrg();

  const savedFormat: DefaultTimeFormatEnum =
    (currentOrg?.defaultTimeFormat as DefaultTimeFormatEnum | undefined) ||
    DEFAULT_TIME_FORMAT;
  const [selectedFormat, setSelectedFormat] =
    useState<DefaultTimeFormatEnum>(savedFormat);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  // Tracks the latest current org id so an in-flight save can gate its
  // toast/error feedback on whether the user is still on the same org.
  const currentOrgIdRef = useRef(currentOrg?.id);

  const hasChanges = selectedFormat !== savedFormat;

  // Reset the form whenever the org changes (id), not only when the saved
  // value changes — otherwise an unsaved selection can leak into the next org.
  useEffect(() => {
    setSelectedFormat(savedFormat);
  }, [currentOrg?.id, savedFormat]);

  useEffect(() => {
    currentOrgIdRef.current = currentOrg?.id;
  }, [currentOrg?.id]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleFormatChange = useCallback((key: Key | null) => {
    if (key === null) {
      return;
    }
    setSelectedFormat(String(key) as DefaultTimeFormatEnum);
    setError(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!currentOrg?.id) {
      setError('Organization not found');
      return;
    }

    const requestOrgId = currentOrg.id;
    const patch: Partial<Team> = { defaultTimeFormat: selectedFormat };
    setIsSaving(true);
    setError(null);

    try {
      const api = new OrgsApi(getApiConfiguration());
      await api.teamsPartialUpdate(
        { id: requestOrgId, patchedTeam: patch },
        { signal: AbortSignal.timeout(30000) }
      );

      // Patch currentOrg + cached availableOrgs atomically (id-guarded in
      // OrgContext) — time labels across the app re-render immediately via
      // useOrgTimeFormat reading currentOrg.defaultTimeFormat.
      updateOrgFields(requestOrgId, patch);

      if (isMountedRef.current && currentOrgIdRef.current === requestOrgId) {
        toast.success('Time format saved');
      }
    } catch (err: unknown) {
      logger.error(
        'Failed to update default time format',
        err instanceof Error ? err : new Error(String(err)),
        { org_id: requestOrgId }
      );
      // Gate error feedback by the request's org id (not just mount state) so
      // a failed save for a since-abandoned org doesn't surface an error
      // against the newly selected org.
      if (isMountedRef.current && currentOrgIdRef.current === requestOrgId) {
        setError("We couldn't save the time format. Try again.");
        toast.error("We couldn't update the time format. Try again.");
      }
    } finally {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [currentOrg, selectedFormat, updateOrgFields]);

  return (
    <div>
      <div className='space-y-4'>
        <Select
          size='sm'
          label='Time format'
          hint='Members can override this in their personal settings.'
          items={TIME_FORMAT_SELECT_ITEMS}
          value={selectedFormat}
          onChange={handleFormatChange}
          isDisabled={isSaving || !canEdit}
        >
          {item => <SelectItem id={item.id}>{item.label}</SelectItem>}
        </Select>

        {!canEdit && (
          <p className='text-sm text-fg-tertiary'>
            Only workspace admins can change the default time format.
          </p>
        )}

        {error && <p className='text-sm text-error-primary'>{error}</p>}
      </div>

      {canEdit && (
        <div className='mt-4 flex justify-end'>
          <Button
            color='primary'
            size='md'
            onClick={handleSave}
            isDisabled={!hasChanges || isSaving}
            isLoading={isSaving}
          >
            Save changes
          </Button>
        </div>
      )}
    </div>
  );
}

export function GeneralSection() {
  const { hasPermissionGroup } = usePermissions();
  const canEdit = hasPermissionGroup(TEAMS_ADMIN_PERMISSION);
  useViewHealthMarker('settings-section-ready');

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>General</h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Manage your workspace name and defaults.
        </p>
      </div>
      <div>
        <OrgNameForm canEdit={canEdit} />
      </div>
      <div>
        <DefaultTimeFormatForm canEdit={canEdit} />
      </div>
    </div>
  );
}
